import { memo, type ReactElement } from 'react';

import {
    CheckDoubleIcon,
    CheckIcon,
    CircleExclamationIcon,
    CrossIcon,
    EditIcon,
    NoneIcon,
    WaitingIcon,
} from '@bestseller-bit/frontend-community.theme.buying-theme';
import { OrderStatesTypes, type OrderState } from '@buying/library';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

interface StateComponentProps {
    /** Pass state from of type OrderState */
    state: Omit<OrderState, 'isDeleted'>;
    /** Will only render the icon without the text */
    iconOnly?: boolean;
}

const OrderStateComponent = memo(
    ({ state, iconOnly }: StateComponentProps): ReactElement => {
        const { palette } = useTheme();

        const stateConfig = {
            [OrderStatesTypes.planned]: {
                icon: WaitingIcon,
                color: palette.text.secondary,
            },
            [OrderStatesTypes.awaitingVendor]: {
                icon: WaitingIcon,
                color: palette.text.secondary,
            },
            [OrderStatesTypes.awaitingSupplier]: {
                icon: WaitingIcon,
                color: palette.text.secondary,
            },
            [OrderStatesTypes.awaitingPurchaser]: {
                icon: WaitingIcon,
                color: palette.text.secondary,
            },
            [OrderStatesTypes.draft]: {
                icon: EditIcon,
                color: palette.text.secondary,
            },
            [OrderStatesTypes.new]: {
                icon: CircleExclamationIcon,
                color: palette.warning.main,
            },
            [OrderStatesTypes.confirmed]: {
                icon: CheckIcon,
                color: palette.text.primary,
            },
            [OrderStatesTypes.completed]: {
                icon: CheckDoubleIcon,
                color: palette.success.main,
            },
            [OrderStatesTypes.rejected]: {
                icon: NoneIcon,
                color: palette.error.main,
            },
            [OrderStatesTypes.cancelled]: {
                icon: CrossIcon,
                color: palette.error.main,
            },
        };

        const config =
            stateConfig[state.orderStateTypeName as OrderStatesTypes];

        const IconComponent = config.icon;

        if (iconOnly) {
            return (
                <IconComponent
                    size={16}
                    color={config.color}
                />
            );
        }

        return (
            <Stack
                direction="row"
                sx={{
                    alignItems: 'center',
                }}
            >
                <IconComponent
                    size={16}
                    color={config.color}
                />
                <Typography
                    variant="buttons.xsmall"
                    sx={{
                        color: config.color,
                        marginLeft: 1,
                    }}
                >
                    {state.orderStateName}
                </Typography>
            </Stack>
        );
    }
);

OrderStateComponent.displayName = 'OrderStateComponent';

export { OrderStateComponent };
